import { FC, SVGProps } from "react";

import { ReactComponent as GearIcon } from "../../../assets/gear.icon.svg";
import { ReactComponent as LinkIcon } from "../../../assets/link.icon.svg";
import { ReactComponent as UsersIcon } from "../../../assets/users.icon.svg";
import { ReactComponent as CubeIcon } from "../../../assets/cube.icon.svg";
import { ReactComponent as FingerprintIcon } from "../../../assets/fingerprint.icon.svg";

/**
 * The single source of truth for the company-scoped admin navigation
 * (assistants_agents_separation_design.md §3.4, Phase C): the gear menu, the
 * Dashboard settings tiles, and page-title.util all derive from this list so
 * an entry can never drift between the three surfaces.
 *
 * Gates mirror the route guards in routers.comp and the server-side guards
 * (§8.2: the UI mirrors the server, never the other way around):
 * - manager: ManagerProtectedRoute (superAdmin | admin | per-KB manager)
 * - admin: AdminProtectedRoute (superAdmin | company admin)
 * - superAdmin: SuperAdminProtectedRoute
 */
export enum AdminNavGate {
    manager = "manager",
    admin = "admin",
    superAdmin = "superAdmin"
}

export interface AdminNavEntry {
    path: string;
    title: string;
    icon: FC<SVGProps<SVGSVGElement>>;
    gate: AdminNavGate;
    /**
     * Tenant admin-feature permission key (tenant_admin_permissions_design.md
     * §6): when set, admins also need `hasAdminFeature(feature)` — super
     * admins bypass inside that helper.
     */
    feature?: string;
}

export const ADMIN_NAV: readonly AdminNavEntry[] = [
    // D8 (Phase C): managers reach the Assistants list; the list scopes rows
    // and the edit dialog gates tabs by role.
    { path: "/assistants", title: "Assistants", icon: CubeIcon, gate: AdminNavGate.manager },
    {
        path: "/agents",
        title: "Agents",
        icon: GearIcon,
        gate: AdminNavGate.admin,
        feature: "agents"
    },
    {
        path: "/user-management",
        title: "User Management",
        icon: UsersIcon,
        gate: AdminNavGate.admin
    },
    {
        path: "/chat-integrations",
        title: "Chat Integrations",
        icon: LinkIcon,
        gate: AdminNavGate.admin,
        feature: "chatIntegrations"
    },
    {
        path: "/integrations",
        title: "Embed Integrations",
        icon: LinkIcon,
        gate: AdminNavGate.admin,
        feature: "embedIntegrations"
    },
    // tenant_auth_delegation_design.md §4.1: per-user permission — the login
    // payload's adminFeatures.authSettings is already ANDed with the grant.
    {
        path: "/authentication",
        title: "Authentication",
        icon: FingerprintIcon,
        gate: AdminNavGate.admin,
        feature: "authSettings"
    },
    {
        path: "/tenant-management",
        title: "Tenant Management",
        icon: UsersIcon,
        gate: AdminNavGate.superAdmin
    }
];

export interface AdminNavFlags {
    /** Manager-inclusive admin flag for the current Assistant (container `isAdmin`). */
    canManage: boolean;
    /** Company-level admin (`user.role === admin`) or super admin. */
    isCompanyAdmin: boolean;
    isSuperAdmin: boolean;
    /** The container's hasAdminFeature; entries without a feature key skip it. */
    hasAdminFeature?: (feature: string) => boolean;
}

export const visibleAdminNav = (flags: AdminNavFlags): AdminNavEntry[] =>
    ADMIN_NAV.filter(entry => {
        const roleOk =
            entry.gate === AdminNavGate.superAdmin
                ? flags.isSuperAdmin
                : entry.gate === AdminNavGate.admin
                  ? flags.isCompanyAdmin
                  : flags.canManage;
        if (!roleOk) return false;
        if (entry.feature && flags.hasAdminFeature) {
            return flags.isSuperAdmin || flags.hasAdminFeature(entry.feature);
        }
        return true;
    });
